import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { auth as appClientAuth } from '@edf-pkg/app-client';
import { store as appMainStore } from '@edf-pkg/app-main';

import { languages, loadNamespaceTranslations } from '$app-web/i18n';
import { errorHandle, getAllowedChatEmbedOrigins } from '$app-web/utils';
import { Cookie } from '$app-web/utils/cookie';

import { StandaloneStudiesMap } from './derive-chat-flags';
import {
    AnswerCallPayload,
    ChatAuthTokenPayload,
    ChatInitPayload,
    ChatStudyChangedPayload,
    HangupCallPayload,
    RejectCallPayload,
    SetCallViewPayload,
} from './types';
import { usePostMessageBridge } from './use-postmessage-bridge';

const ACTION_SOURCE = 'standalone-chat';
const HANDSHAKE_TIMEOUT_MS = 15000;
const ACCESS_TOKEN_COOKIE = 'access_token';
const REFRESH_TOKEN_COOKIE = 'refresh_token';
// The standalone chat always runs in the pdash dashboard, so its translation keys
// (call_started, write_a_message, etc.) live in the `pdash` namespace.
const CHAT_NAMESPACE = 'pdash';
const FALLBACK_LANGUAGE = 'en-US';

// Resolve the parent-provided locale to a supported language code that actually has `pdash`
// translations, mirroring the i18n saga's `checkAndSetLanguage`. Falls back to en-US otherwise.
function resolveChatLanguage(locale?: string): string {
    if (!locale || !languages.languageExist(locale)) {
        return FALLBACK_LANGUAGE;
    }
    const normalized = languages.normalizeSupportedLanguage(locale);
    if (normalized === null || !languages.get(normalized).namespaceHasTranslations(CHAT_NAMESPACE)) {
        return FALLBACK_LANGUAGE;
    }
    return normalized;
}

export interface ReadyState {
    studyId: number;
    studies: StandaloneStudiesMap;
}

export interface CallCommandHandlers {
    onAnswerCall: (payload: AnswerCallPayload) => void;
    onRejectCall: (payload: RejectCallPayload) => void;
    onHangupCall: (payload: HangupCallPayload) => void;
    onSetCallView: (payload: SetCallViewPayload) => void;
}

export type HandshakeStatus = 'loading' | 'timed-out' | 'ready';

type SendToParent = ReturnType<typeof usePostMessageBridge>['sendToParent'];

interface HandshakeResult {
    status: HandshakeStatus;
    readyState: ReadyState | null;
    sendToParent: SendToParent;
    callCommandHandlersRef: React.MutableRefObject<CallCommandHandlers | null>;
}

export function useHandshake(): HandshakeResult {
    const dispatch = useDispatch();
    const matrixPassword = useSelector(appMainStore.userDuck.duckSelectors.matrixPasswordSelector);
    const userId = useSelector(appMainStore.userDuck.duckSelectors.idSelector);
    const isUserLoaded = Boolean(matrixPassword) && Boolean(userId);

    const [authReceived, setAuthReceived] = useState(false);
    const [readyState, setReadyState] = useState<ReadyState | null>(null);
    const [hasSignaledReady, setHasSignaledReady] = useState(false);
    const [handshakeTimedOut, setHandshakeTimedOut] = useState(false);

    const allowedOrigins = useMemo(() => getAllowedChatEmbedOrigins(), []);

    // Inner component sets this ref. A null ref means chat hasn't mounted, so there is no
    // matrix call to act on — any command (answer, reject, hangup, set-view) is safely a no-op.
    const callCommandHandlersRef = useRef<CallCommandHandlers | null>(null);

    const applyAuth = useCallback(
        (accessToken: string, refreshToken: string, expiresIn?: number) => {
            appClientAuth.useAuthenticationStore.getState().updateState({
                accessToken,
                refreshToken,
                ...(expiresIn != null ? { expiresIn } : {}),
            });

            dispatch(
                appMainStore.userDuck.duckActionCreators.setUserDataExternal({
                    userData: { apiKey: accessToken },
                    actionSource: ACTION_SOURCE,
                    shouldPersistUserData: true,
                    shouldLoadFromAPI: true,
                    shouldSetInitializationStatus: true,
                })
            );

            setAuthReceived(true);
        },
        [dispatch]
    );

    const handleAuthToken = useCallback(
        (payload: ChatAuthTokenPayload) => {
            applyAuth(payload.accessToken, payload.refreshToken, payload.expiresIn);
        },
        [applyAuth]
    );

    // Same-origin staging: hydrate auth from shared new-PDash cookies before initializeApp
    // would redirect to /sign-in. Split-origin local dev has no cookies → no-op.
    useLayoutEffect(() => {
        const accessToken = Cookie.getItem(ACCESS_TOKEN_COOKIE);
        const refreshToken = Cookie.getItem(REFRESH_TOKEN_COOKIE);
        if (accessToken && refreshToken) {
            applyAuth(accessToken, refreshToken);
        }
    }, [applyAuth]);

    const handleInit = useCallback(async (payload: ChatInitPayload) => {
        // The i18n saga that normally loads namespace resources is gated on a space handshake the
        // standalone space deliberately bypasses, so `t()` would otherwise return raw keys. Load the
        // chat (pdash) bundle for the parent's locale here, before the chat mounts, so messages and
        // the composer render translated on first paint.
        try {
            await loadNamespaceTranslations(resolveChatLanguage(payload.locale), CHAT_NAMESPACE);
        } catch (error) {
            errorHandle.anError(error as Error);
        }
        setReadyState({ studyId: payload.studyId, studies: payload.studies });
    }, []);

    const handleStudyChanged = useCallback((payload: ChatStudyChangedPayload) => {
        setReadyState({ studyId: payload.studyId, studies: payload.studies });
    }, []);

    const handleAnswerCall = useCallback((payload: AnswerCallPayload) => {
        callCommandHandlersRef.current?.onAnswerCall(payload);
    }, []);

    const handleRejectCall = useCallback((payload: RejectCallPayload) => {
        callCommandHandlersRef.current?.onRejectCall(payload);
    }, []);

    const handleHangupCall = useCallback((payload: HangupCallPayload) => {
        callCommandHandlersRef.current?.onHangupCall(payload);
    }, []);

    const handleSetCallView = useCallback((payload: SetCallViewPayload) => {
        callCommandHandlersRef.current?.onSetCallView(payload);
    }, []);

    const { sendToParent } = usePostMessageBridge(allowedOrigins, {
        onAuthToken: handleAuthToken,
        onInit: handleInit,
        onStudyChanged: handleStudyChanged,
        onAnswerCall: handleAnswerCall,
        onRejectCall: handleRejectCall,
        onHangupCall: handleHangupCall,
        onSetCallView: handleSetCallView,
    });

    useEffect(() => {
        sendToParent({ type: 'CHAT_FRAME_READY' });
    }, [sendToParent]);

    const isFullyReady = authReceived && isUserLoaded && readyState !== null;

    useEffect(() => {
        if (isFullyReady && !hasSignaledReady) {
            sendToParent({ type: 'CHAT_READY' });
            setHasSignaledReady(true);
        }
    }, [isFullyReady, hasSignaledReady, sendToParent]);

    useEffect(() => {
        if (isFullyReady) return undefined;
        const id = window.setTimeout(() => {
            setHandshakeTimedOut(true);
            sendToParent({
                type: 'CHAT_ERROR',
                payload: { message: 'Handshake timed out', code: 'HANDSHAKE_TIMEOUT' },
            });
        }, HANDSHAKE_TIMEOUT_MS);
        return () => window.clearTimeout(id);
    }, [isFullyReady, sendToParent]);

    let status: HandshakeStatus;
    if (isFullyReady) {
        status = 'ready';
    } else if (handshakeTimedOut) {
        status = 'timed-out';
    } else {
        status = 'loading';
    }

    return { status, readyState, sendToParent, callCommandHandlersRef };
}
